(() => {
  if (customElements.get('jf-catalog')) return;
  customElements.define('jf-catalog', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.events = new AbortController();
      this.selected = new Map();
      this.baseTitle = this.querySelector('h1').textContent;
      this.cards = [...this.querySelectorAll('[data-family]')];
      this.secondaryIntro = this.querySelector('[data-secondary-intro]');
      this.search = this.querySelector('[data-search]');
      this.categoryFilter = this.querySelector('[data-category-filter]');
      this.styleFilter = this.querySelector('[data-style-filter]');
      this.filterPanel = this.querySelector('[data-filter-panel]');
      this.sort = this.querySelector('[data-sort]');
      this.dialog = this.querySelector('[data-compare-dialog]');
      const signal = this.events.signal;
      this.cards.forEach((card, order) => {
        card.models = [...card.querySelectorAll('[data-model]')];
        const featured = card.models.find(model => model.dataset.model === this.dataset.featured);
        const second = card.models.find(model => model.dataset.model === this.dataset.second);
        card.dataset.order = String(featured ? -2 : second ? -1 : order);
        const primary = card.models.find(model => model.dataset.primary === 'true');
        if (featured || second || primary) this.showModel(card, featured || second || primary);
        const select = card.querySelector('[data-layout-select]');
        select?.addEventListener('change', () => {
          const model = card.models.find(item => item.dataset.model === select.value);
          if (!model) return;
          this.showModel(card, model);
          if (this.selected.has(card.dataset.family)) this.selected.set(card.dataset.family, model);
          this.updateComparison();
          this.sortCards();
        }, { signal });
        card.querySelector('[data-compare-select]')?.addEventListener('change', event => {
          if (event.target.checked && this.selected.size >= 3) {
            event.target.checked = false;
            this.querySelector('[data-compare-status]').textContent = this.dataset.limitMessage;
            return;
          }
          if (event.target.checked) this.selected.set(card.dataset.family, card.models.find(model => !model.hidden));
          else this.selected.delete(card.dataset.family);
          this.updateComparison();
        }, { signal });
      });
      this.querySelectorAll('[data-enhanced]').forEach(element => { element.hidden = false; });
      this.search.addEventListener('input', () => { this.filter(); this.saveURL(false); }, { signal });
      this.styleFilter?.addEventListener('change', () => { this.filter(); this.saveURL(true); }, { signal });
      this.categoryFilter?.addEventListener('change', () => { this.category = this.categoryFilter.value; this.filter(); this.saveURL(true); }, { signal });
      const mobile = window.matchMedia('(max-width:649px)');
      if (this.filterPanel) this.filterPanel.open = !mobile.matches;
      mobile.addEventListener('change', event => { if (this.filterPanel) this.filterPanel.open = !event.matches; }, { signal });
      this.sort.addEventListener('change', () => { this.sortCards(); this.saveURL(true); }, { signal });
      this.querySelectorAll('[data-filter]').forEach(button => button.addEventListener('click', () => {
        this.category = button.dataset.filter;
        this.filter();
        this.saveURL(true);
      }, { signal }));
      this.querySelector('[data-compare-open]').addEventListener('click', () => this.openComparison(), { signal });
      this.querySelector('[data-compare-close]').addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog.addEventListener('close', () => this.querySelector('[data-compare-open]').focus(), { signal });
      this.querySelector('[data-compare-clear]').addEventListener('click', () => {
        this.selected.clear();
        this.cards.forEach(card => { card.querySelector('[data-compare-select]').checked = false; });
        this.updateComparison();
      }, { signal });
      window.addEventListener('popstate', () => this.readURL(), { signal });
      this.readURL();
      this.updateComparison();
    }
    disconnectedCallback() {
      this.events?.abort();
      if (this.dialog?.open) this.dialog.close();
      this.ready = false;
    }
    isSupporting(model) { return ['tables', 'tv', 'addons'].includes(model?.dataset.category); }
    isSeating(model) { return ['sectionals', 'sets', 'sofas'].includes(model.dataset.category); }
    readURL() {
      const params = new URL(location.href).searchParams;
      const category = params.get('category');
      this.category = this.categoryFilter && [...this.categoryFilter.options].some(option => option.value === category) ? category : 'all';
      if (this.categoryFilter) this.categoryFilter.value = this.category;
      this.search.value = params.get('q') || '';
      if (this.styleFilter) this.styleFilter.value = ['led', 'tufted', 'other'].includes(params.get('style')) ? params.get('style') : 'all';
      this.sort.value = ['low', 'high'].includes(params.get('sort')) ? params.get('sort') : 'featured';
      this.filter();
    }
    saveURL(push) {
      const url = new URL(location.href);
      const values = { category: this.category === 'all' ? '' : this.category, q: this.search.value.trim(), style: !this.styleFilter || this.styleFilter.value === 'all' ? '' : this.styleFilter.value, sort: this.sort.value === 'featured' ? '' : this.sort.value };
      for (const [key, value] of Object.entries(values)) value ? url.searchParams.set(key, value) : url.searchParams.delete(key);
      if (url.href !== location.href) history[push ? 'pushState' : 'replaceState']({}, '', url);
      document.querySelectorAll('[data-jf-search] [name="q"]').forEach(input => { input.value = this.search.value; });
    }
    showModel(card, model) {
      card.dataset.merchRole = this.isSupporting(model) ? 'secondary' : 'primary';
      card.models.forEach(item => { item.hidden = item !== model; });
      const select = card.querySelector('[data-layout-select]');
      if (select) {
        select.value = model.dataset.model;
        model.querySelector('.jf-catalog__body').insertBefore(select.closest('label'), model.querySelector('.jf-catalog__price'));
      }
      const compare = card.querySelector('[data-compare-select]');
      compare.closest('label').hidden = !this.isSeating(model);
      compare.setAttribute('aria-label', `${compare.closest('label').textContent.trim()} — ${model.dataset.layout}`);
    }
    filter() {
      const term = this.search.value.trim().toLowerCase();
      const style = this.styleFilter?.value || 'all';
      if (this.dataset.collection === 'all') this.querySelector('h1').textContent = this.category === 'tables-tv' ? this.dataset.tablesTitle : this.baseTitle;
      let count = 0;
      this.querySelectorAll('[data-filter]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.filter === this.category)));
      for (const card of this.cards) {
        const matches = card.models.filter(model => {
          const category = this.category === 'all' || (this.category === 'seating' ? this.isSeating(model) : this.category === 'tables-tv' ? ['tables','tv'].includes(model.dataset.category) : model.dataset.category === this.category);
          const styleMatches = !this.isSeating(model) || style === 'all' || model.dataset.style === style;
          return category && styleMatches && model.dataset.name.includes(term);
        });
        card.hidden = matches.length === 0;
        if (card.hidden) continue;
        count++;
        const model = matches.find(item => !item.hidden) || matches[0];
        this.showModel(card, model);
        if (this.selected.has(card.dataset.family)) this.selected.set(card.dataset.family, model);
        const select = card.querySelector('[data-layout-select]');
        if (select) {
          [...select.options].forEach(option => { option.hidden = !matches.some(item => item.dataset.model === option.value); option.disabled = option.hidden; });
          select.closest('label').hidden = matches.length < 2;
        }
      }
      this.querySelector('[data-count]').textContent = String(count);
      this.querySelector('[data-empty]').hidden = count > 0;
      this.sortCards();
    }
    sortCards() {
      const direction = this.sort.value === 'low' ? 1 : -1;
      const mainFirst = this.dataset.collection === 'all' && this.category === 'all';
      const activeModel = card => card.models.find(model => !model.hidden);
      const ordered = [...this.cards].sort((a, b) => {
        if (mainFirst) {
          const priority = Number(this.isSupporting(activeModel(a))) - Number(this.isSupporting(activeModel(b)));
          if (priority) return priority;
        }
        return this.sort.value === 'featured'
          ? Number(a.dataset.order) - Number(b.dataset.order)
          : direction * (Number(activeModel(a)?.dataset.price || 0) - Number(activeModel(b)?.dataset.price || 0));
      });
      const grid = this.querySelector('[data-grid]');
      ordered.forEach(card => grid.append(card));
      if (this.secondaryIntro) {
        const secondary = ordered.find(card => !card.hidden && this.isSupporting(activeModel(card)));
        const hasPrimary = ordered.some(card => !card.hidden && !this.isSupporting(activeModel(card)));
        this.secondaryIntro.hidden = !mainFirst || !secondary || !hasPrimary;
        if (secondary) grid.insertBefore(this.secondaryIntro, secondary);
      }
    }
    updateComparison() {
      const size = this.selected.size;
      this.querySelector('[data-compare-bar]').hidden = size === 0;
      this.querySelector('[data-compare-count]').textContent = this.dataset.countLabel.replace('__COUNT__', String(size));
      this.querySelector('[data-compare-open]').disabled = size < 2;
      this.querySelector('[data-compare-status]').textContent = '';
    }
    openComparison() {
      if (this.selected.size < 2) return;
      const head = this.querySelector('[data-compare-head]');
      const rows = [...this.querySelectorAll('[data-compare-row]')];
      [head, ...rows].forEach(row => { while (row.children.length > 1) row.lastElementChild.remove(); });
      for (const model of this.selected.values()) {
        const th = document.createElement('th');
        th.scope = 'col';
        const cover = model.querySelector('.jf-catalog__image img');
        if (cover) th.append(cover.cloneNode(true));
        const name = document.createElement('strong');
        name.textContent = model.querySelector('[data-compare-name]').textContent;
        const layout = document.createElement('span');
        layout.textContent = model.dataset.layout;
        th.append(name, layout);
        head.append(th);
        for (const row of rows) {
          const td = document.createElement('td');
          const key = row.dataset.compareRow;
          const content = model.querySelector(`[data-compare-${key}]`);
          if (content) td.append(content.cloneNode(true));
          if (key === 'price') td.append(model.querySelector('[data-compare-variant]').cloneNode(true));
          if (key === 'included') td.querySelectorAll('details').forEach(details => { details.open = true; });
          td.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
          row.append(td);
        }
      }
      this.dialog.showModal();
    }
  });
})();
